/** @jsxImportSource @emotion/react */

import { ReactNode, memo } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { CircularProgress, Typography, Backdrop } from "@material-ui/core";

import { Button, Icon } from "..";

import { container, content, center as centerStyle, backdrop, errorWrapper, btn } from "./styles";

export type State = "loading" | "loading-mask" | "error" | "success" | "empty" | number;

interface Props {
  children?: ReactNode;
  state: State;
  loadingSize?: number;
  center?: boolean;
  errorMessage?: string;
  emptyMessage?: string;
  className?: string;
  onClickRetry?: () => void;
}

const ViewState = ({
  children,
  state,
  loadingSize,
  center,
  errorMessage,
  emptyMessage,
  className,
  onClickRetry,
}: Props): JSX.Element => {
  const intl = useIntl();

  return (
    <div css={container}>
      {state !== "loading-mask" && state !== "success" && (
        <div css={[content, center && centerStyle]} className={className}>
          {state === "loading" && <CircularProgress size={loadingSize} />}
          {(state === "error" || typeof state === "number") && (
            <div css={errorWrapper}>
              <Icon name="exclamation-mark" color="primary" fontSize="large" />
              <Typography variant="body1">
                {errorMessage ||
                  `${intl.formatMessage({ id: "general.error" })}${typeof state === "number" ? ` (${state})` : ""}`}
              </Typography>
              {onClickRetry && (
                <Button css={btn} color="primary" variant="contained" onClick={onClickRetry}>
                  <FormattedMessage id="btn.retry" />
                </Button>
              )}
            </div>
          )}
          {state === "empty" && (
            <Typography variant="body1">{emptyMessage || <FormattedMessage id="view-state.empty" />}</Typography>
          )}
        </div>
      )}
      {state === "loading-mask" && (
        <Backdrop css={backdrop} open>
          <CircularProgress size={loadingSize} />
        </Backdrop>
      )}
      {(state === "success" || state === "loading-mask") && children}
    </div>
  );
};

export default memo(ViewState);
